import { ReactElement, useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { ConfirmDeleteModal, CrudMode, DeleteErrorException, ValueLists } from '@ohpen/enso-ui';

import { BdmResponse, RegionResponse, bdmDelete, bdmReplace } from '@pmp/api';

import { getApiErrorDetail } from '@pmp/utils';

import { getInitialState, reducer } from '../reducer';
import { BDMS_LIST, REGIONS_LIST, BdmRow } from '../regionsBdmsTableConfig';
import { BdmsList } from './BdmsList';
import { BdmCrudForm } from './BdmCrudForm';
import { ReplaceForm } from './ReplaceForm';

interface Props {
  regions: RegionResponse[];
  bdms: BdmResponse[];
  loading: boolean;
  refresh: () => void;
}

export const BdmPanel = ({ regions, bdms, loading, refresh }: Props): ReactElement => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer<BdmResponse>, getInitialState<BdmResponse>());
  const [replace, setReplace] = useState<{ open: boolean; record: BdmResponse | null }>({ open: false, record: null });

  const bdmNameById = useMemo(() => new Map(bdms.map(b => [b.id, b.name])), [bdms]);
  const regionNameById = useMemo(() => new Map(regions.map(r => [r.id, r.name])), [regions]);

  const valueLists: ValueLists = useMemo(
    () => ({
      [BDMS_LIST]: Object.fromEntries(bdms.map(b => [b.id, b.name])),
      [REGIONS_LIST]: Object.fromEntries(regions.map(r => [r.id, r.name])),
    }),
    [bdms, regions],
  );

  const dataSource: BdmRow[] = useMemo(
    () => bdms.map(b => ({ ...b, regionNames: (b.regionIds ?? []).map(id => regionNameById.get(id) ?? id) })),
    [bdms, regionNameById],
  );

  const deleteRecord = state.deleteModal.record;
  const onConfirmDelete = useCallback(async () => {
    if (!deleteRecord) return { description: '' };
    try {
      await bdmDelete({ path: { id: deleteRecord.id } });
    } catch (error) {
      dispatch({ type: 'closeDeleteModal' });
      return Promise.reject(new DeleteErrorException(getApiErrorDetail(error) ?? t('lender.regionsBdms.labels.bdmDeleteBlockedDetail')));
    }
    refresh();
    dispatch({ type: 'closeDeleteModal' });
    return { description: t('lender.regionsBdms.labels.bdmDeleted', { name: deleteRecord.name }) };
  }, [deleteRecord, refresh, t]);

  const replaceRecord = replace.record;

  return (
    <>
      <BdmsList
        data={dataSource}
        valueLists={valueLists}
        loading={loading}
        canReplace={bdms.length >= 2}
        onCreate={() => dispatch({ type: 'openCrud', mode: CrudMode.CREATE })}
        onEdit={record => dispatch({ type: 'openCrud', mode: CrudMode.EDIT, record })}
        onReplace={record => setReplace({ open: true, record })}
        onDelete={record => dispatch({ type: 'openDeleteModal', record })}
      />

      {state.crud.open && (
        <BdmCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          regions={regions}
          onClose={() => dispatch({ type: 'closeCrud' })}
          refreshTable={refresh}
        />
      )}

      {replace.open && replaceRecord && (
        <ReplaceForm
          title={t('lender.regionsBdms.labels.replaceBdm', { name: replaceRecord.name })}
          explanation={t('lender.regionsBdms.labels.replaceBdmExplanation')}
          replacementLabelKey="lender.regionsBdms.labels.replacementBdm"
          requiredMessage={t('lender.regionsBdms.labels.replacementBdmRequired')}
          options={bdms.filter(b => b.id !== replaceRecord.id).map(b => ({ label: b.name, value: b.id }))}
          onReplace={replacementBdmId => bdmReplace({ path: { id: replaceRecord.id }, body: { replacementBdmId } })}
          successMessage={rid =>
            t('lender.regionsBdms.labels.bdmReplaced', { name: replaceRecord.name, replacement: bdmNameById.get(rid) ?? '' })
          }
          onClose={() => setReplace({ open: false, record: null })}
          refreshTable={refresh}
        />
      )}

      <ConfirmDeleteModal
        open={state.deleteModal.open}
        title={t('lender.regionsBdms.labels.deleteBdm')}
        okText={t('global.buttons.delete')}
        onConfirm={onConfirmDelete}
        onCancel={() => dispatch({ type: 'closeDeleteModal' })}
      >
        {deleteRecord && (
          <>
            <Typography.Paragraph>{t('lender.regionsBdms.labels.deleteBdmConfirm', { name: deleteRecord.name })}</Typography.Paragraph>
            <Typography.Paragraph>{t('lender.regionsBdms.labels.noChangeRequestWarning')}</Typography.Paragraph>
          </>
        )}
      </ConfirmDeleteModal>
    </>
  );
};
